'use client';

import { Button, Flex, Spinner, Text, PinInput, Field } from '@chakra-ui/react';

import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { useCallback, useContext, useEffect, useState } from 'react';

import { differenceInSeconds, isAfter } from 'date-fns';
import { AuthContext } from '@/contexts/AuthContext';
import ErrorMessage from '@/components/ErrorMessage';

type FormObject = {
  code: string[];
};

const schema = z.object({
  code: z
    .array(z.string().min(1), { error: 'O código é obrigatório' })
    .length(6, { message: 'O código deve ter exatamente 6 números' }),
});

let timer: NodeJS.Timeout;

type UserConfirmAccountProps = {
  successAction: () => void;
};

const UserConfirmAccount = ({ successAction }: UserConfirmAccountProps) => {
  const [errorMessage, setErrorMessage] = useState('');
  const [buttonEnable, setButtonEnable] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const [timeNow, setTimeNow] = useState('');

  const { confirmAccountData, resendConfirmation, confirmationEmail } =
    useContext(AuthContext);

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<FormObject>({ resolver: zodResolver(schema) });

  const onSubmit = useCallback(
    async (data: FormObject) => {
      try {
        if (!confirmAccountData.id) return;

        setIsLoading(true);

        await confirmationEmail(
          confirmAccountData.id,
          data.code.join(''),
          () => setIsLoading(false),
          successAction,
        );
      } catch (error) {
        const e = error as { message: string };

        setErrorMessage(e.message);
      }
    },
    [confirmationEmail, confirmAccountData, successAction],
  );

  const handleResendCode = useCallback(() => {
    try {
      setIsLoading(true);
      setTimeNow('');
      if (confirmAccountData.id) {
        resendConfirmation(confirmAccountData.id, () => setIsLoading(false));
      }

      setButtonEnable(false);
    } catch (error) {
      const e = error as { message: string };

      setErrorMessage(e.message);
    }
  }, [confirmAccountData, resendConfirmation]);

  useEffect(() => {
    if (confirmAccountData && confirmAccountData.expireDate) {
      if (!buttonEnable) {
        timer = setInterval(() => {
          if (!confirmAccountData.expireDate) return;

          const now = new Date();

          const result = isAfter(now, confirmAccountData.expireDate);

          if (result) setButtonEnable(result);

          const totalSeconds = differenceInSeconds(
            confirmAccountData.expireDate,
            now,
          );
          const minutes = Math.floor(totalSeconds / 60);
          const seconds = totalSeconds % 60;

          setTimeNow(`${minutes}:${seconds.toString().padStart(2, '0')}`);
        }, 1000);
      } else {
        clearInterval(timer);
      }
    }

    return () => {
      clearInterval(timer);
    };
  }, [buttonEnable, confirmAccountData]);

  return (
    <Flex
      as="form"
      flexDir="column"
      gap="10px"
      onSubmit={handleSubmit(onSubmit)}
      w="full"
      align="center"
    >
      <Flex flexDir="column" gap="4px" pb="2px" w="full">
        <Text
          fontSize="11px"
          fontWeight="700"
          letterSpacing="0.8px"
          textTransform="uppercase"
          color={{ base: 'rgba(0,0,0,0.4)', _dark: 'rgba(255,255,255,0.35)' }}
        >
          Verificação
        </Text>
        <Text
          fontSize="18px"
          fontWeight="700"
          color={{ base: '#0A1B2B', _dark: '#E2EFF5' }}
        >
          Confirmar Conta
        </Text>
      </Flex>
      <Text
        fontSize="13px"
        textAlign="center"
        w="80%"
        color={{ base: 'rgba(0,0,0,0.6)', _dark: 'rgba(255,255,255,0.5)' }}
      >
        Foi enviado para o seu e-mail um código de confirmação da sua conta.
      </Text>

      <Field.Root
        w="full"
        alignItems="center"
        justifyContent="center"
        display="flex"
        invalid={!!errors.code}
      >
        <Controller
          control={control}
          name="code"
          render={({ field }) => (
            <PinInput.Root
              disabled={isLoading}
              value={field.value}
              onValueChange={(e) => field.onChange(e.value)}
              onValueComplete={(e) => {
                field.onChange(e.value);
                document.getElementById('submit_code')?.click();
              }}
            >
              <PinInput.HiddenInput />
              <PinInput.Control>
                <PinInput.Input index={0} />
                <PinInput.Input index={1} />
                <PinInput.Input index={2} />
                <PinInput.Input index={3} />
                <PinInput.Input index={4} />
                <PinInput.Input index={5} />
              </PinInput.Control>
            </PinInput.Root>
          )}
        />
        {errors.code && (
          <Field.ErrorText>{errors.code?.message}</Field.ErrorText>
        )}
      </Field.Root>
      <button id="submit_code" type="submit" style={{ display: 'none' }}>
        Submit
      </button>
      {errorMessage && <ErrorMessage message={errorMessage} />}

      <Button
        h="44px"
        w="full"
        borderRadius="full"
        bg={buttonEnable ? '#00B0CA' : 'transparent'}
        color={
          buttonEnable
            ? 'white'
            : { base: 'rgba(0,0,0,0.4)', _dark: 'rgba(255,255,255,0.35)' }
        }
        border={buttonEnable ? 'none' : '1.5px solid'}
        borderColor={{ base: 'rgba(0,0,0,0.12)', _dark: 'rgba(255,255,255,0.1)' }}
        fontWeight="600"
        fontSize="14px"
        boxShadow={buttonEnable ? '0 4px 20px rgba(0,176,202,0.3)' : 'none'}
        disabled={!buttonEnable}
        onClick={handleResendCode}
        _hover={buttonEnable ? { bg: '#009AB5' } : {}}
        _disabled={{ opacity: 0.6, cursor: 'not-allowed' }}
        transition="all 0.2s"
      >
        {buttonEnable ? (
          'Reenviar Código'
        ) : (
          <Flex align="center" gap="6px">
            {timeNow ? (
              <>
                <Text fontSize="12px" opacity="0.7">
                  Reenviar em
                </Text>
                <Text fontSize="13px" fontWeight="700" color="#00B0CA">
                  {timeNow}
                </Text>
              </>
            ) : (
              <Spinner size="sm" />
            )}
          </Flex>
        )}
      </Button>
    </Flex>
  );
};

export default UserConfirmAccount;
